import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const NotificationDropdown = () => {
  // Estado para detectar si estamos en móvil
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 768; // Considerar móvil si ancho <= 768px
  
  // Manejar cambios en el tamaño de la ventana
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { notifications, unreadCount, markAsRead, markAllAsRead, createNotification } = useNotifications();
  const { currentUser } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    // Navigate to related page
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }

    setIsOpen(false);
  };

  const handleSeeAllClick = () => {
    // Determine navigation path based on user role
    const userRole = currentUser?.role || 'client';
    const notificationsPath = userRole === 'admin' ? '/admin/notifications' : '/notifications';
    navigate(notificationsPath);
    setIsOpen(false);
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  // Test function to create a sample notification
  const createTestNotification = async () => {
    if (!currentUser) return;
    
    console.log('Creating test notification for user:', currentUser.uid);
    await createNotification({
      userId: currentUser.uid,
      title: 'Test Notification',
      message: 'This is a test notification to verify the system is working',
      icon: '🔔',
      category: 'system',
      priority: 'medium',
      relatedId: 'test-id',
      relatedType: 'test',
      actionUrl: '/notifications'
    });
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';
    
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#f44336';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return '#666';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'appointment': return '#2196f3';
      case 'estimate': return '#4caf50';
      case 'payment': return '#9c27b0';
      case 'system': return '#607d8b';
      default: return '#666';
    }
  };

  // Show only recent notifications in dropdown (max 5)
  const recentNotifications = notifications.slice(0, 5);

  return (
    <div style={{ position: 'relative' }} ref={dropdownRef}>
      {/* Bell Icon with Badge */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background-color 0.2s ease'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <img 
          src="/notification.png" 
          alt="Notifications" 
          style={{ 
            width: '28px', 
            height: '28px', 
            objectFit: 'contain'
          }} 
        />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '0',
            right: '0',
            backgroundColor: '#f44336',
            color: 'white',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            border: '2px solid white'
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Overlay para móvil */}
          {isMobile && (
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 999
              }}
              onClick={() => setIsOpen(false)}
            />
          )}
          <div style={{
            position: isMobile ? 'fixed' : 'absolute',
            top: isMobile ? '50%' : '100%',
            right: isMobile ? '50%' : '0',
            left: isMobile ? '50%' : 'auto',
            transform: isMobile ? 'translate(-50%, -50%)' : 'none',
            backgroundColor: 'white',
            border: '1px solid #ddd',
            borderRadius: isMobile ? '12px' : '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            width: isMobile ? '90%' : '350px',
            maxWidth: isMobile ? '90%' : '350px',
            maxHeight: isMobile ? '80vh' : '500px',
            overflowY: 'auto',
            zIndex: 1000
          }}>
            {/* Header */}
            <div style={{
              padding: '1rem',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f8f9fa',
              position: isMobile ? 'sticky' : 'relative',
              top: 0,
              zIndex: 2
            }}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>
                Notifications
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {/* Temporary test button */}
            
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#007bff',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}
                  >
                    Mark all read
                  </button>
                )}
              </div>
            </div>

            {/* Notifications List */}
            <div style={{ maxHeight: isMobile ? 'calc(80vh - 110px)' : '300px', overflowY: 'auto' }}>
              {recentNotifications.length > 0 ? (
                recentNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    style={{
                      padding: '1rem',
                      borderBottom: '2px solid #f0f0f0',
                      cursor: 'pointer',
                      backgroundColor: notification.read ? 'white' : '#f8f9ff',
                      transition: 'background-color 0.2s ease',
                      position: 'relative'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = notification.read ? '#f8f9fa' : '#f0f0ff';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = notification.read ? 'white' : '#f8f9ff';
                    }}
                  >
                    {/* Unread indicator */}
                    {!notification.read && (
                      <div style={{
                        position: 'absolute',
                        left: '0.5rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '8px',
                        height: '8px',
                        backgroundColor: '#007bff',
                        borderRadius: '50%'
                      }} />
                    )}

                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginLeft: !notification.read ? '1rem' : '0' }}>
                      {/* Icon */}
                      <div style={{
                        fontSize: '1.5rem',
                        flexShrink: 0
                      }}>
                        {notification.icon}
                      </div>

                      {/* Content */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '0.25rem'
                        }}>
                          <h4 style={{
                            margin: 0,
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            color: '#333'
                          }}>
                            {notification.title}
                          </h4>
                          <span style={{
                            fontSize: '0.75rem',
                            color: getCategoryColor(notification.category),
                            fontWeight: '500',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {notification.category}
                          </span>
                        </div>

                        <p style={{
                          margin: '0 0 0.5rem 0',
                          fontSize: '0.8rem',
                          color: '#666',
                          lineHeight: '1.4'
                        }}>
                          {notification.message}
                        </p>

                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <span style={{
                            fontSize: '0.75rem',
                            color: '#999'
                          }}>
                            {formatTimeAgo(notification.createdAt)}
                          </span>
                          
                          {notification.priority && (
                            <span style={{
                              fontSize: '0.7rem',
                              color: getPriorityColor(notification.priority),
                              fontWeight: '500',
                              textTransform: 'uppercase'
                            }}>
                              {notification.priority}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{
                  padding: '2rem',
                  textAlign: 'center',
                  color: '#666'
                }}>
                  <img 
                    src="/notification.png" 
                    alt="No notifications" 
                    style={{ 
                      width: '64px', 
                      height: '64px', 
                      objectFit: 'contain',
                      marginBottom: '1rem'
                    }} 
                  />
                  <p style={{ margin: 0, fontSize: '0.875rem' }}>No notifications yet</p>
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div style={{
                padding: '0.75rem 1rem',
                borderTop: '1px solid #eee',
                backgroundColor: '#f8f9fa',
                position: isMobile ? 'sticky' : 'relative',
                bottom: 0,
                zIndex: 2
              }}>
                <button
                  onClick={handleSeeAllClick}
                  style={{
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    color: '#007bff',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    padding: '0.5rem',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(0, 123, 255, 0.1)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  See All Notifications
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationDropdown;
